import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  Calendar, 
  ClipboardList, 
  Swords, 
  Scale,
  UserCheck,
  FileText,
  ArrowRight,
  ArrowLeft,
  X,
  Sparkles,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

const tourSteps = [
  {
    title: "WELCOME TO BLACK ARROW",
    subtitle: "Let's get you started",
    description: "We've set up your gym with sample data to help you explore. This quick tour will show you the key features.",
    icon: Sparkles,
    color: "red"
  },
  {
    title: "FIGHTER PROFILES",
    subtitle: "Manage your roster",
    description: "Track all your fighters with detailed profiles including weight class, record, stance, and more. We've added a demo fighter to get you started.",
    icon: Users,
    color: "blue",
    link: "/fighters"
  },
  {
    title: "TRAINING CAMPS",
    subtitle: "Prepare for fights",
    description: "Organize training camps for upcoming fights. Set focus areas, track progress, and keep detailed notes.",
    icon: Calendar,
    color: "green",
    link: "/camps"
  },
  {
    title: "ASSESSMENTS",
    subtitle: "Track fighter skills",
    description: "Rate fighters on striking, grappling, wrestling, and cardio. Compare progress over time.",
    icon: ClipboardList,
    color: "purple",
    link: "/assessments"
  },
  {
    title: "FIGHT SCHEDULE",
    subtitle: "Upcoming bouts",
    description: "Manage fight bookings, track results, and maintain a complete fight history for each fighter.",
    icon: Swords,
    color: "yellow",
    link: "/fights"
  },
  {
    title: "WEIGHT TRACKING",
    subtitle: "Monitor weight cuts",
    description: "Log daily weights and track trends leading up to weigh-ins. Critical for making weight safely.",
    icon: Scale,
    color: "cyan",
    link: "/weight-tracking"
  },
  {
    title: "ATTENDANCE",
    subtitle: "Track check-ins",
    description: "Use scan cards or manual check-in to track class attendance. See who's putting in the work.",
    icon: UserCheck,
    color: "emerald",
    link: "/attendance"
  },
  {
    title: "REPORTS",
    subtitle: "Export PDF reports",
    description: "Generate professional PDF reports for fighters, gym overview, attendance, and training camps.",
    icon: FileText,
    color: "orange",
    link: "/reports"
  },
  {
    title: "YOU'RE ALL SET!",
    subtitle: "Start building champions",
    description: "Explore the demo data we've added, then add your real fighters and start tracking their journey to the top.",
    icon: CheckCircle,
    color: "green"
  }
];

const colorMap = {
  red: "bg-red-600/20 border-red-600/50 text-red-500",
  blue: "bg-blue-600/20 border-blue-600/50 text-blue-500",
  green: "bg-green-600/20 border-green-600/50 text-green-500",
  purple: "bg-purple-600/20 border-purple-600/50 text-purple-500",
  yellow: "bg-yellow-600/20 border-yellow-600/50 text-yellow-500",
  cyan: "bg-cyan-600/20 border-cyan-600/50 text-cyan-500",
  emerald: "bg-emerald-600/20 border-emerald-600/50 text-emerald-500",
  orange: "bg-orange-600/20 border-orange-600/50 text-orange-500"
};

export const OnboardingTour = ({ onComplete }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = tourSteps[currentStep];
  const isLastStep = currentStep === tourSteps.length - 1;
  const isFirstStep = currentStep === 0;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleSkip = async () => {
    await handleComplete();
  };

  const handleComplete = async () => {
    try {
      await axios.post(`${API}/api/auth/complete-onboarding`);
    } catch (error) {
      console.error('Failed to mark onboarding complete:', error);
    }
    setIsVisible(false);
    setTimeout(() => {
      onComplete?.();
    }, 300);
  };

  const handleGoToFeature = () => {
    if (step.link) {
      handleComplete();
      navigate(step.link);
    }
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      >
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.3 }}
          className="bg-ba-surface border border-ba-border rounded-sm max-w-lg w-full overflow-hidden"
        >
          {/* Header */}
          <div className="p-6 border-b border-ba-border flex items-center justify-between">
            <div className="flex items-center gap-2">
              {tourSteps.map((_, index) => (
                <div
                  key={index}
                  className={cn(
                    "w-2 h-2 rounded-full transition-colors",
                    index === currentStep ? "bg-red-600" : 
                    index < currentStep ? "bg-green-600" : "bg-zinc-700"
                  )}
                />
              ))}
            </div>
            <button
              onClick={handleSkip}
              className="text-zinc-500 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-8 text-center">
            <div className={cn(
              "w-20 h-20 rounded-full border-2 flex items-center justify-center mx-auto mb-6",
              colorMap[step.color]
            )}>
              <step.icon className="w-10 h-10" />
            </div>
            
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-2">
              {step.subtitle}
            </p>
            <h2 className="font-heading text-3xl text-white tracking-tight mb-4">
              {step.title}
            </h2>
            <p className="text-zinc-400 font-body leading-relaxed">
              {step.description}
            </p>

            {step.link && (
              <Button
                variant="outline"
                onClick={handleGoToFeature}
                className="mt-4 border-ba-border text-zinc-300 hover:bg-ba-surface hover:border-red-600/50"
              >
                Explore {step.title.toLowerCase()} →
              </Button>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-ba-border flex items-center justify-between">
            <div>
              {!isFirstStep && (
                <Button
                  variant="ghost"
                  onClick={handlePrev}
                  className="text-zinc-400 hover:text-white"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
              )}
            </div>
            
            <div className="flex items-center gap-3">
              {!isLastStep && (
                <Button
                  variant="ghost"
                  onClick={handleSkip}
                  className="text-zinc-500 hover:text-zinc-300"
                >
                  Skip Tour
                </Button>
              )}
              <Button
                onClick={handleNext}
                className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider transition-all"
              >
                {isLastStep ? "Get Started" : "Next"}
                {!isLastStep && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default OnboardingTour;
